
import React from 'react';
import { AppView } from '../types';
import { Home, Users, HeartPulse, Map, LayoutGrid } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  currentView: AppView;
  onChangeView: (view: AppView) => void;
  onResetView?: () => void;
  disableScroll?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({ children, currentView, onChangeView, onResetView, disableScroll = false }) => {
  // Don't show global nav on onboarding or auth
  const showNav = currentView !== AppView.ONBOARDING &&
    currentView !== AppView.AUTH;

  const handleNavClick = (view: AppView) => {
    // Haptic feedback
    if (navigator.vibrate) navigator.vibrate(10);

    // If clicking the already active tab, trigger reset to go back to landing/home state
    if (view === currentView && onResetView) {
      onResetView();
    }

    onChangeView(view);
  };

  return (
    <div className="w-full bg-stone-50 text-stone-900 flex flex-col overflow-hidden antialiased" style={{ height: '100dvh' }}>
      {/* 
         Device Frame:
         On Desktop: Centered card with margin.
         On Mobile: Full screen.
      */}
      <main className="flex-1 w-full max-w-md mx-auto relative bg-white sm:shadow-xl sm:my-8 sm:rounded-[2rem] sm:border sm:border-stone-100 overflow-hidden flex flex-col" style={{ height: '100%', maxHeight: '100dvh' }}>

        {/* Content Area */}
        <div className={`flex-1 relative flex flex-col ${disableScroll ? 'overflow-hidden' : 'overflow-y-auto no-scrollbar scroll-smooth'}`}>
          {/* View Transition Wrapper */}
          <div key={currentView} className="flex-1 flex flex-col h-full animate-fade-in">
            {children}
          </div>
        </div>

        {showNav && (
          <nav role="navigation" aria-label="Main navigation" className="border-t border-stone-100 bg-white/95 backdrop-blur-md pb-safe z-50 shrink-0">
            <div className="flex justify-around items-center h-16 px-2">
              <NavButton
                active={currentView === AppView.DASHBOARD}
                onClick={() => handleNavClick(AppView.DASHBOARD)}
                icon={<Home size={24} />}
                label="Today"
              />
              <NavButton
                active={currentView === AppView.JOURNEY}
                onClick={() => handleNavClick(AppView.JOURNEY)}
                icon={<Map size={24} />}
                label="Journey"
              />
              <NavButton
                active={currentView === AppView.TOOLS}
                onClick={() => handleNavClick(AppView.TOOLS)}
                icon={<LayoutGrid size={24} />}
                label="Tools"
              />
              <NavButton
                active={currentView === AppView.COMMUNITY}
                onClick={() => handleNavClick(AppView.COMMUNITY)}
                icon={<Users size={24} />}
                label="Village"
              />
              <NavButton
                active={currentView === AppView.LABOR_MODE}
                onClick={() => handleNavClick(AppView.LABOR_MODE)}
                icon={<HeartPulse size={24} />}
                label="Labor"
                highlight
              />
            </div>
          </nav>
        )}
      </main>
    </div>
  );
};

interface NavButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  highlight?: boolean;
}

const NavButton: React.FC<NavButtonProps> = ({ active, onClick, icon, label, highlight }) => {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`flex flex-col items-center justify-center w-full h-full transition-all duration-200 active:scale-90 ${active
        ? highlight ? 'text-rose-600' : 'text-brand-700'
        : 'text-stone-700 hover:text-stone-900'
        }`}
    >
      <div className={`p-1 rounded-xl transition-all ${active && 'bg-stone-50 scale-110'} ${highlight && !active && 'text-rose-400'}`}>
        {icon}
      </div>
      <span className="text-[11px] font-semibold mt-1">{label}</span>
    </button>
  );
};
